import React, { useCallback, useEffect, useState } from "react";
import { useTelegram } from "../../../hooks/useTelegram";
import TelegramScreen from "../../../components/kit/Screen/TelegramScreen";
import TelegramHeader from "../../../components/kit/Header/TelegramHeader";
import TelegramText from "../../../components/kit/Text/TelegramText";
import TelegramInput from "../../../components/kit/Input/TelegramInput";
import TelegramButton from "../../../components/kit/Button/TelegramButton";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAlert } from "../../../components/providers/AlertProvider";
import StoreIndicator from "../../../components/kit/StoreIndicator/StoreIndicator";
import Loader from "../../../components/kit/Loader/Loader";
import TimeSlotCards from "../../../components/kit/TimeSlotCards/TimeSlotCards";
import "../../../components/kit/MiniMultiInputForm/TelegramMiniMultiInputForm.css";
import "../../../App.css";

const { submitUserDetailAmendment } = require("../../../db/db");

// Function to calculate available time slots
const getAvailableTimeSlots = (deliveryTimeSlots) => {
  const availableSlots = [];
  const daysToShow = 14; // Number of days to show time slots
  const currentDateTime = new Date();
  const currentTimeInMinutes =
    currentDateTime.getHours() * 60 + currentDateTime.getMinutes();

  let remainingSlots = 2; // Maximum number of slots to include
  let slotsAdded = 0;

  for (let i = 0; i < daysToShow && slotsAdded < remainingSlots; i++) {
    const currentDate = new Date();
    currentDate.setDate(currentDateTime.getDate() + i);
    const currentDay = currentDate.getDate();
    const currentMonth = currentDate.toLocaleString("default", {
      month: "short",
    });
    const currentYear = currentDate.getFullYear();

    for (const slot of deliveryTimeSlots) {
      const timePeriod = slot.timeperiod;
      const timeRange = slot.timeslot;
      const deliveryFromHour = parseInt(slot.deliveryfromtime);
      const cutoffHour = parseInt(slot.cutofftimehour);
      const cutoffMin = parseInt(slot.cutofftimemin);

      // Calculate the cutoff time in minutes
      const cutoffTimeInMinutes = cutoffHour * 60 + cutoffMin;

      // Check if the slot is still available for today
      if (i === 0 && currentTimeInMinutes >= cutoffTimeInMinutes) {
        continue; // Skip this slot for today as cutoff time has passed
      }

      const timeslotDate = new Date(
        currentYear,
        currentDate.getMonth(),
        currentDay,
        deliveryFromHour,
        0,
        0
      );

      const timeSlot = {
        id: slot.id,
        text: `${currentDay} ${currentMonth} ${currentYear} - ${
          timeRange || slot.timeslot
        }`,
        datetime: timeslotDate,
        deliveryFromHour: deliveryFromHour,
        timePeriod: timePeriod,
        timeRange: timeRange,
        date: `${currentDay} ${currentMonth} ${currentYear}`,
      };

      availableSlots.push(timeSlot);
      slotsAdded++;

      if (slotsAdded >= remainingSlots) break;
    }
  }

  return availableSlots;
};

const UserDetailEditScreen = () => {
  const { webApp, user, executeMethod } = useTelegram();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { triggerAlert } = useAlert();

  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [updateComplete, setUpdateComplete] = useState(false);

  // Form state for user details
  const [formData, setFormData] = useState({
    customerName: "",
    customerContact: "",
    customerAddress: "",
    remarks: "",
  });

  // Timeslot state
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(-1);
  const [originalTimeSlot, setOriginalTimeSlot] = useState(null);
  const [originalTimeSlotIndex, setOriginalTimeSlotIndex] = useState(-1);
  const [disabledTimeSlots, setDisabledTimeSlots] = useState([]);

  // Get order data from navigation state or Redux store
  const existingOrder = useSelector((state) => state.existingOrder);
  const sessionToken = useSelector((state) => state.sessionToken);
  const selectedStore = useSelector((state) => state.selectedStore);
  const deliveryTimeSlots = useSelector((state) => state.deliveryTimeSlots);

  // Calculate available time slots from Redux state
  const availableTimeSlots = useSelector((state) => state.deliveryTimeSlots);

  // Get order details from location state or existing order in store
  const orderData = location.state?.orderData || existingOrder;

  useEffect(() => {
    if (orderData) {
      // Pre-populate form with existing order data
      setFormData({
        customerName: orderData.customerName || "",
        customerContact: orderData.customerContact || "",
        customerAddress: orderData.customerAddress || "",
        remarks: orderData.remarks || "",
      });
    } else {
      console.error("[UserDetailEditScreen] No order data available");
      triggerAlert("Error: No order data available for editing");
      navigate(-1); // Go back
    }
  }, [orderData, navigate, triggerAlert]);

  // Set original timeslot based on current order data
  useEffect(() => {
    if (orderData && orderData.deliveryDate && orderData.timeSlot?.timeRange) {
      const originalDate = new Date(orderData.deliveryDate);
      const originalSlot = `${originalDate.getDate()} ${originalDate.toLocaleString(
        "default",
        { month: "short" }
      )} ${originalDate.getFullYear()} - ${orderData.timeSlot.timeRange}`;
      setOriginalTimeSlot(originalSlot);
    }
  }, [orderData]);

  // Identify current timeslot and mark it as disabled
  useEffect(() => {
    if (
      orderData &&
      orderData.deliveryDate &&
      availableTimeSlots.length > 0
    ) {
      const currentDate = new Date(orderData.deliveryDate);
      const currentDateStr = `${currentDate.getDate()} ${currentDate.toLocaleString(
        "default",
        { month: "short" }
      )} ${currentDate.getFullYear()}`;


      // Find the index of the current timeslot in available options
      const currentSlotIndex = availableTimeSlots.findIndex((slot, index) => {
        // Extract date and time from slot
        let slotDate = "";
        let slotTimeRange = "";

        if (slot.datetime && slot.timeRange) {
          // New format with datetime and timeRange
          const slotDateObj = new Date(slot.datetime);
          slotDate = `${slotDateObj.getDate()} ${slotDateObj.toLocaleString(
            "default",
            { month: "short" }
          )} ${slotDateObj.getFullYear()}`;
          slotTimeRange = slot.timeRange;
        } else if (slot.text) {
          // Old format with text field
          const parts = slot.text.split(" - ");
          slotDate = parts[0];
          slotTimeRange = parts.slice(1).join(" - ");
        }

        // Compare dates and time ranges
        // Use the timeRange from timeSlot object (e.g., "3PM-7PM")
        const orderTimeRange = orderData.timeSlot?.timeRange;
        const isDateMatch = slotDate === currentDateStr;
        const isTimeMatch = slotTimeRange === orderTimeRange ||
                           (orderTimeRange && slotTimeRange.includes(orderTimeRange));


        return isDateMatch && isTimeMatch;
      });

      if (currentSlotIndex !== -1) {
        setDisabledTimeSlots([currentSlotIndex]);
        // Pre-select the current timeslot for better UX
        setOriginalTimeSlotIndex(currentSlotIndex);
        setSelectedTimeSlot(currentSlotIndex);
      }
    }
  }, [orderData, availableTimeSlots]);

  // Handle completion and WebApp closing
  const onMainClick = useCallback(() => {
    executeMethod("close", webApp.close, true);
  }, [executeMethod]);

  const onBackClick = useCallback(() => {
    executeMethod("close", webApp.close, true);
  }, [executeMethod]);

  useEffect(() => {
    if (updateComplete) {
      webApp.ready();
      webApp.disableClosingConfirmation();
      webApp.MainButton.hideProgress(true);
      webApp.MainButton.enable();

      webApp.MainButton.onClick(onMainClick);
      webApp.BackButton.onClick(onBackClick);

      webApp.MainButton.text = "THANK YOU";
      webApp.MainButton.color = "#31b545";

      webApp.MainButton.show();
      webApp.BackButton.hide();

      if (!webApp.isExpanded) {
        executeMethod("expand", webApp.expand, true);
      }

      return () => {
        webApp.MainButton.offClick(onMainClick);
        webApp.BackButton.offClick(onBackClick);
      };
    }
  }, [updateComplete, webApp, onMainClick, onBackClick, executeMethod]);

  // Handle input changes
  const handleInputChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear validation error for this field
      if (validationErrors[field]) {
        setValidationErrors((prev) => ({
          ...prev,
          [field]: null,
        }));
      }
    },
    [validationErrors]
  );

  // Handle timeslot change
  const handleTimeSlotChange = useCallback((slotIndex) => {
    setSelectedTimeSlot(slotIndex);
  }, []);

  // Validate form data
  const validateForm = useCallback(() => {
    const errors = {};

    const officialPostalCodePrefixes = [
      // Central
      "01",
      "02",
      "03",
      "04",
      "05",
      "06",
      "07",
      "08",
      "09",
      "10",
      "14",
      "15",
      "16",
      "17",
      "18",
      "19",
      "20",
      "21",
      "22",
      "23",
      "24",
      "25",
      "26",
      "27",
      "28",
      "29",
      "30",
      "31",
      "32",
      "33",
      "34",
      "35",
      "36",
      "37",

      // East
      "38",
      "39",
      "40",
      "41",
      "42",
      "43",
      "44",
      "45",
      "46",
      "47",
      "48",
      "49",
      "50",
      "51",
      "52",
      "81",

      // West
      "11",
      "12",
      "13",
      "58",
      "59",
      "60",
      "61",
      "62",
      "63",
      "64",
      "65",
      "66",
      "67",
      "68",
      "69",
      "70",
      "71",
      "72",
      "73",

      // North
      "53",
      "54",
      "55",
      "56",
      "57",
      "75",
      "76",
      "77",
      "78",
      "79",
      "80",
      "82",

      // Self-Collection
      "99",
    ];

    // Customer name validation
    if (!formData.customerName.trim()) {
      errors.customerName = "Customer name is required";
    } else if (
      !/^[A-Za-z ]+$/.test(formData.customerName.trim()) ||
      formData.customerName.trim() === ""
    ) {
      // Display an error message or prevent the user from proceeding
      errors.customerContact = "Please input a valid name."; // Display an error message or prevent the user from proceeding
      return;
    }

    // Customer contact validation
    if (!formData.customerContact.trim()) {
      errors.customerContact = "Customer contact is required";
    } else if (!/^(\+|\d)(\d*)?$/.test(formData.customerContact.trim())) {
      errors.customerContact = "Phone No. can only start with '+' or a digit.";
    }

    // Customer address validation
    if (!formData.customerAddress.trim()) {
      errors.customerAddress = "Customer address is required";
    } else if (
      !/^\d{6}$/.test(formData.customerAddress.trim()) ||
      formData.customerAddress.trim() === "" ||
      !officialPostalCodePrefixes.some((prefix) =>
        formData.customerAddress.trim().startsWith(prefix)
      )
    ) {
      errors.customerAddress = "Please input a valid postal code.";
    }

    // Remarks validation (optional)
    if (formData.remarks && formData.remarks.length > 500) {
      errors.remarks = "Remarks must be less than 500 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // Check if any changes were made
  const hasChanges = useCallback(() => {
    if (!orderData) return false;

    // Check basic user detail changes
    const hasUserDetailChanges =
      formData.customerName !== (orderData.customerName || "") ||
      formData.customerContact !== (orderData.customerContact || "") ||
      formData.customerAddress !== (orderData.customerAddress || "") ||
      formData.remarks !== (orderData.remarks || "");

    // Check timeslot changes - only if actually different from original
    const hasTimeslotChanges =
      selectedTimeSlot >= 0 &&
      availableTimeSlots[selectedTimeSlot] &&
      selectedTimeSlot !== originalTimeSlotIndex;

    return hasUserDetailChanges || hasTimeslotChanges;
  }, [formData, orderData, selectedTimeSlot, availableTimeSlots, originalTimeSlotIndex]);

  // Handle save changes
  const handleSaveChanges = useCallback(async () => {
    if (!validateForm()) {
      triggerAlert("Please fix the validation errors before saving");
      return;
    }

    if (!hasChanges()) {
      triggerAlert("No changes detected to save");
      return;
    }

    try {
      setIsLoading(true);

      const userProfile = {
        userId: user?.id,
        userName: user?.username,
      };

      const payload = {
        Header: {
          UserDetails: userProfile,
          OrderId: orderData.id,
        },
        Body: {
          UserDetailAmendments: {
            customerName: formData.customerName.trim(),
            customerContact: formData.customerContact.trim(),
            customerAddress: formData.customerAddress.trim(),
            remarks: formData.remarks.trim(),
            // Include timeslot data only if genuinely changed from original
            ...(selectedTimeSlot >= 0 &&
              availableTimeSlots[selectedTimeSlot] &&
              selectedTimeSlot !== originalTimeSlotIndex && {
                deliveryDate: availableTimeSlots[selectedTimeSlot].datetime,
                timeSlot: availableTimeSlots[selectedTimeSlot],
              }),
          },
        },
        storeId: selectedStore?.storeId || selectedStore?.id, // Move storeId to root level
      };

      const result = await submitUserDetailAmendment(payload, sessionToken);

      // Check for nested success response structure
      const actualSuccess =
        result.data?.success !== undefined
          ? result.data.success
          : result.success;

      if (actualSuccess === true) {
        const responseData = result.data || result;
        const message =
          responseData.data?.message ||
          responseData.message ||
          "Customer details have been updated successfully";

        // Show success state and prepare to close
        setUpdateComplete(true);
      } else if (actualSuccess === false) {
        // Handle specific failure cases (like duplicates)
        const responseData = result.data || result;
        const errorMessage =
          responseData.message ||
          responseData.error ||
          result.message ||
          result.error ||
          "Failed to update customer details";
        triggerAlert(errorMessage, "error");
      } else {
        // Fallback for unexpected response structure
        triggerAlert("Unexpected response format", "error");
      }
    } catch (error) {
      console.error(
        "[UserDetailEditScreen] Error submitting user detail amendment:",
        error
      );

      let errorMessage = "An error occurred while updating customer details";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      triggerAlert(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  }, [
    validateForm,
    hasChanges,
    formData,
    orderData,
    user,
    sessionToken,
    triggerAlert,
    navigate,
  ]);

  // Handle cancel/back
  const handleCancel = useCallback(() => {
    if (hasChanges()) {
      const confirmDiscard = window.confirm(
        "You have unsaved changes. Are you sure you want to discard them?"
      );
      if (!confirmDiscard) {
        return;
      }
    }
    navigate(-1);
  }, [hasChanges, navigate]);

  if (!orderData) {
    return (
      <TelegramScreen className="userDetailEditScreen">
        <StoreIndicator store={selectedStore} />
        <TelegramHeader className="colorWhite">
          Edit Customer Details
        </TelegramHeader>
        <TelegramText className="error">
          No order data available for editing
        </TelegramText>
      </TelegramScreen>
    );
  }

  // Show thank you screen after successful update
  if (updateComplete) {
    return (
      <TelegramScreen showbackbutton={0} className="parent-container">
        <StoreIndicator store={selectedStore} />
        <TelegramHeader>
          <TelegramText className="header-text">
            ⭐️ Customer Details Updated ⭐️
          </TelegramText>
        </TelegramHeader>
        <TelegramText className="centered-text big-text">
          Your order details have been successfully updated
        </TelegramText>
        <TelegramText className="centered-text big-text">
          You will receive an updated order confirmation shortly
        </TelegramText>
      </TelegramScreen>
    );
  }

  return (
    <TelegramScreen className="user-detail-edit-screen userDetailEditScreen">
      <StoreIndicator store={selectedStore} />

      <TelegramHeader className="colorWhite">
        Edit Customer Details
      </TelegramHeader>

      <TelegramText className="colorWhite mb-3">
        Order #{orderData.orderNo || orderData.id}
      </TelegramText>

      {isLoading && <Loader />}

      {/* Use PaymentInfoScreen-style layout */}
      <div className="TelegramMiniMultiInputForm">
        {/* Row 1: Customer Name (full width) */}
        <div className="form-row">
          <div className="form-field-full">
            <TelegramText className="telegramSubtitle">
              Customer Name
            </TelegramText>
            <TelegramInput
              type="text"
              value={formData.customerName}
              onChange={(e) =>
                handleInputChange("customerName", e.target.value)
              }
              placeholder="Enter customer name"
              className={validationErrors.customerName ? "error" : ""}
            />
            {validationErrors.customerName && (
              <TelegramText className="error-message">
                {validationErrors.customerName}
              </TelegramText>
            )}
          </div>
        </div>

        {/* Row 2: Phone and Address (50/50 split) */}
        <div className="form-row">
          <div className="form-field-half">
            <TelegramText className="telegramSubtitle">Phone No.</TelegramText>
            <TelegramInput
              type="tel"
              value={formData.customerContact}
              onChange={(e) =>
                handleInputChange("customerContact", e.target.value)
              }
              placeholder="8888 8888"
              className={validationErrors.customerContact ? "error" : ""}
            />
            {validationErrors.customerContact && (
              <TelegramText className="error-message">
                {validationErrors.customerContact}
              </TelegramText>
            )}
          </div>
          <div className="form-field-half">
            <TelegramText className="telegramSubtitle">Address</TelegramText>
            <TelegramInput
              type="text"
              value={formData.customerAddress}
              onChange={(e) =>
                handleInputChange("customerAddress", e.target.value)
              }
              placeholder="Full address with postal code"
              className={validationErrors.customerAddress ? "error" : ""}
            />
            {validationErrors.customerAddress && (
              <TelegramText className="error-message">
                {validationErrors.customerAddress}
              </TelegramText>
            )}
          </div>
        </div>

        {/* Row 3: Remarks (full width) */}
        <div className="form-row">
          <div className="form-field-full">
            <TelegramText className="telegramSubtitle">
              Remarks (Optional)
            </TelegramText>
            <TelegramInput
              type="text"
              value={formData.remarks}
              onChange={(e) => handleInputChange("remarks", e.target.value)}
              placeholder="Any special instructions or notes"
              className={validationErrors.remarks ? "error" : ""}
            />
            {validationErrors.remarks && (
              <TelegramText className="error-message">
                {validationErrors.remarks}
              </TelegramText>
            )}
            <TelegramText className="colorGray help-text">
              {formData.remarks.length}/500 characters
            </TelegramText>
          </div>
        </div>

        {/* Row 4: Timeslot Selection */}
        <div className="form-row">
          <div className="form-field-full">
            <TelegramText className="telegramSubtitle">
              Delivery Timeslot
            </TelegramText>
            {availableTimeSlots.length > 0 ? (
              <div className="user-detail-edit-timeslots">
                <TimeSlotCards
                  optionslabel=""
                  options={availableTimeSlots}
                  selectedSlot={selectedTimeSlot}
                  onChange={handleTimeSlotChange}
                />
              </div>
            ) : (
              <TelegramText className="colorGray">
                No timeslot changes available at this time
              </TelegramText>
            )}
            {selectedTimeSlot >= 0 &&
             availableTimeSlots[selectedTimeSlot] &&
             selectedTimeSlot !== originalTimeSlotIndex && (
              <TelegramText className="colorOrange help-text">
                ⚠️ Changing timeslot may affect delivery arrangements
              </TelegramText>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons - PaymentInfoScreen style */}
      <div className="checkout__comment__container">
        <TelegramButton
          onClick={handleSaveChanges}
          disabled={isLoading || !hasChanges()}
          className={`save-button ${
            !hasChanges() || isLoading ? "disabled" : ""
          }`}
        >
          {isLoading ? "Saving..." : "Save Changes"}
        </TelegramButton>

        <TelegramButton
          onClick={handleCancel}
          disabled={isLoading}
          className="cancel-button"
        >
          Cancel
        </TelegramButton>
      </div>

      {/* Changes indicator */}
      {hasChanges() && (
        <TelegramText className="colorOrange changes-indicator">
          ⚠️ You have unsaved changes
        </TelegramText>
      )}
    </TelegramScreen>
  );
};

export default UserDetailEditScreen;
